import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import {
  Sparkles, X, Loader2, Wand2, Check, ChevronRight, Eye, ArrowLeft,
  Clock, Users, Award, HelpCircle, Ticket, BarChart3, Calendar as CalendarIcon, Type
} from 'lucide-react';
import EventPageBlocks from './EventPageBlocks';
import { readableApiError, insufficientFundsInfo } from '../utils/apiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLOCK_ICONS = {
  countdown: Clock, schedule: CalendarIcon, lineup: Users, sponsors: Award,
  faq: HelpCircle, cta: Ticket, stats: BarChart3, text: Type,
};

export default function AIEventGenerator({ open, onClose, onCreated }) {
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [step, setStep] = useState(1); // 1 input, 2 generating, 3 review
  const [form, setForm] = useState({
    name: '', event_date: '', end_date: '', venue: '', address: '', city: 'denver', hint: '',
  });
  const [generated, setGenerated] = useState(null);
  const [applying, setApplying] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  if (!open) return null;

  const handleGenerate = async () => {
    if (!form.name.trim() || !form.event_date) {
      toast.error('Name and event date are required');
      return;
    }
    setStep(2);
    try {
      const res = await axios.post(`${API}/events/ai-generator/generate`, form, { headers });
      setGenerated(res.data);
      setStep(3);
      if (res.data.fallback) toast.warning('AI unavailable — using fallback. Edit before applying.');
      else toast.success('Generated! Review & apply.');
    } catch (e) {
      const funds = insufficientFundsInfo(e);
      toast.error(readableApiError(e, 'Generation failed'), funds ? {
        action: { label: 'Top up wallet', onClick: () => navigate(funds.topup_url) },
        duration: 8000,
      } : undefined);
      setStep(1);
    }
  };

  const handleApply = async () => {
    if (!generated) return;
    setApplying(true);
    try {
      const res = await axios.post(`${API}/events/ai-generator/apply`, {
        base_inputs: form,
        event_fields: generated.event_fields,
        page_blocks: generated.page_blocks,
        enable_page_builder: true,
      }, { headers });
      toast.success('Event created with page builder!');
      onCreated?.(res.data.event_id);
      onClose();
      navigate(`/admin/events/${res.data.event_id}/page-builder`);
    } catch (e) {
      const funds = insufficientFundsInfo(e);
      toast.error(readableApiError(e, 'Apply failed'), funds ? {
        action: { label: 'Top up wallet', onClick: () => navigate(funds.topup_url) },
        duration: 8000,
      } : undefined);
    } finally { setApplying(false); }
  };

  const updateField = (key, val) => setGenerated(g => ({ ...g, event_fields: { ...g.event_fields, [key]: val } }));

  const reset = () => { setStep(1); setGenerated(null); setPreviewMode(false); };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ai-event-modal">
      <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-indigo-500 to-emerald-500">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold">AI Event Generator</h3>
              <p className="text-xs text-muted-foreground">
                {step === 1 && 'Step 1 of 3 · Tell me the basics'}
                {step === 2 && 'Step 2 of 3 · Researching & writing…'}
                {step === 3 && 'Step 3 of 3 · Review & apply'}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} disabled={step === 2 || applying}><X className="h-4 w-4" /></Button>
        </div>

        {/* Step 1: form */}
        {step === 1 && (
          <div className="overflow-y-auto p-6 space-y-4" data-testid="ai-step-1">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 space-y-1.5">
                <Label className="text-sm">Event Name *</Label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Mile High Rooftop Jazz & Cocktails" data-testid="ai-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Start *</Label>
                <Input type="datetime-local" value={form.event_date} onChange={e => setForm(p => ({ ...p, event_date: e.target.value }))} data-testid="ai-start" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">End (optional)</Label>
                <Input type="datetime-local" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} data-testid="ai-end" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Venue</Label>
                <Input value={form.venue} onChange={e => setForm(p => ({ ...p, venue: e.target.value }))} placeholder="The Source Hotel" data-testid="ai-venue" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Address</Label>
                <Input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} placeholder="3330 Brighton Blvd, Denver, CO" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">City</Label>
                <select className="w-full h-10 px-3 border border-border rounded-md bg-background text-sm" value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} data-testid="ai-city">
                  <option value="denver">Denver</option>
                  <option value="charlotte">Charlotte</option>
                  <option value="los_angeles">Los Angeles</option>
                  <option value="las_vegas">Las Vegas</option>
                </select>
              </div>
              <div className="sm:col-span-2 space-y-1.5">
                <Label className="text-sm">Anything else? (vibe, theme, headliners, must-haves) — optional</Label>
                <Textarea value={form.hint} onChange={e => setForm(p => ({ ...p, hint: e.target.value }))} placeholder="Sunset jazz with craft cocktails, 21+ event, partnering with two local distilleries" className="min-h-[70px]" data-testid="ai-hint" />
              </div>
            </div>

            <div className="rounded-lg p-3 border border-indigo-500/20 bg-indigo-500/5 text-xs flex items-start gap-2">
              <Wand2 className="h-3.5 w-3.5 text-indigo-400 mt-0.5 flex-shrink-0" />
              <p>I'll research your past events on the platform, study your audience's preferences, then generate the description, tagline, FAQ, schedule, lineup, sponsors, countdown, stats, and CTA — all in conversion-optimized order.</p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={handleGenerate} className="bg-gradient-to-r from-indigo-600 to-emerald-600 hover:opacity-90 text-white" data-testid="ai-generate-btn">
                <Sparkles className="h-4 w-4 mr-1.5" /> Generate Event <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: generating */}
        {step === 2 && (
          <div className="flex-1 flex flex-col items-center justify-center p-12 space-y-4" data-testid="ai-step-2">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-indigo-500 to-emerald-500 blur-2xl opacity-50 animate-pulse" />
              <div className="relative p-6 rounded-full bg-gradient-to-br from-indigo-500 to-emerald-500">
                <Sparkles className="h-10 w-10 text-white animate-pulse" />
              </div>
            </div>
            <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
            <div className="text-center space-y-1 max-w-md">
              <p className="font-semibold">Generating your event…</p>
              <p className="text-xs text-muted-foreground">Researching past events · Studying competition · Writing copy · Pre-filling blocks</p>
            </div>
          </div>
        )}

        {/* Step 3: review */}
        {step === 3 && generated && (
          <>
            <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/20 flex-wrap gap-2">
              <div className="flex items-center gap-2 text-xs flex-wrap">
                <Badge variant="outline" className="bg-emerald-500/10 border-emerald-500/30 text-emerald-500">
                  <Check className="h-2.5 w-2.5 mr-0.5" /> Generated
                </Badge>
                {generated.fallback && <Badge variant="outline" className="bg-amber-500/10 border-amber-500/30 text-amber-500">Fallback (AI unavailable)</Badge>}
                <span className="text-muted-foreground">Researched {generated.research?.past_events_used?.length || 0} past events</span>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={() => setPreviewMode(p => !p)} data-testid="ai-preview-toggle">
                  {previewMode ? <><ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to edit</> : <><Eye className="h-3.5 w-3.5 mr-1" /> Preview</>}
                </Button>
                <Button size="sm" variant="outline" onClick={reset}><Sparkles className="h-3.5 w-3.5 mr-1" /> Regenerate</Button>
              </div>
            </div>

            {previewMode ? (
              <div className="overflow-y-auto p-6 flex-1 bg-card" data-testid="ai-preview">
                <h2 className="text-3xl font-bold">{form.name}</h2>
                <p className="text-lg text-muted-foreground mt-1">{generated.event_fields.tagline}</p>
                <p className="text-sm mt-4 whitespace-pre-wrap">{generated.event_fields.description}</p>
                <EventPageBlocks blocks={generated.page_blocks.filter(b => b.is_visible)} event={{ event_date: form.event_date, ...form }} />
              </div>
            ) : (
              <div className="overflow-y-auto p-5 flex-1 grid lg:grid-cols-2 gap-5" data-testid="ai-step-3">
                {/* Editable event fields */}
                <Card className="border-border">
                  <CardContent className="p-4 space-y-3">
                    <h4 className="font-semibold text-sm border-b border-border pb-2">Event Fields</h4>
                    <FieldEdit label="Tagline" value={generated.event_fields.tagline} onChange={v => updateField('tagline', v)} />
                    <FieldEdit label="Short description" value={generated.event_fields.short_description} onChange={v => updateField('short_description', v)} />
                    <div className="space-y-1">
                      <Label className="text-xs">Description</Label>
                      <Textarea value={generated.event_fields.description} onChange={e => updateField('description', e.target.value)} className="min-h-[140px] text-sm" data-testid="ai-edit-description" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <FieldEdit label="Category" value={generated.event_fields.category} onChange={v => updateField('category', v)} />
                      <FieldEdit label="Age" value={generated.event_fields.age_restriction} onChange={v => updateField('age_restriction', v)} />
                      <FieldEdit label="Dress code" value={generated.event_fields.dress_code} onChange={v => updateField('dress_code', v)} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Tags</Label>
                      <div className="flex flex-wrap gap-1.5">
                        {(generated.event_fields.tags || []).map((t, i) => (
                          <Badge key={i} variant="outline" className="text-[10px]">{t}</Badge>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Highlights</Label>
                      <ul className="text-xs space-y-1 list-disc pl-4">
                        {(generated.event_fields.highlights || []).map((h, i) => <li key={i}>{h}</li>)}
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                {/* Generated blocks list */}
                <Card className="border-border">
                  <CardContent className="p-4 space-y-2">
                    <h4 className="font-semibold text-sm border-b border-border pb-2">Page Blocks ({generated.page_blocks.length})</h4>
                    {generated.page_blocks.map((b, i) => {
                      const Icon = BLOCK_ICONS[b.type] || Type;
                      const itemsCount = (b.data?.items || []).length;
                      return (
                        <div key={b.block_id} className="border border-border rounded-md p-2.5 flex items-center gap-2" data-testid={`ai-block-${b.type}`}>
                          <span className="text-[10px] font-mono text-muted-foreground w-5">#{i + 1}</span>
                          <div className="p-1.5 rounded bg-indigo-500/10">
                            <Icon className="h-3.5 w-3.5 text-indigo-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium capitalize">{b.type} {b.data?.heading && <span className="text-muted-foreground font-normal">— {b.data.heading}</span>}</p>
                            {itemsCount > 0 && <p className="text-[10px] text-muted-foreground">{itemsCount} item{itemsCount > 1 ? 's' : ''}</p>}
                          </div>
                          <button onClick={() => setGenerated(g => ({ ...g, page_blocks: g.page_blocks.filter(x => x.block_id !== b.block_id) }))} className="text-rose-500 text-xs" title="Remove">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      );
                    })}
                    <p className="text-[11px] text-muted-foreground pt-2">You can fine-tune each block in the Page Builder after applying.</p>
                  </CardContent>
                </Card>
              </div>
            )}

            <div className="flex justify-between gap-2 p-5 border-t border-border bg-muted/20">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={handleApply} disabled={applying} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ai-apply-btn">
                {applying ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Creating…</> : <><Check className="h-4 w-4 mr-1.5" /> Create Event & Open Page Builder</>}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const FieldEdit = ({ label, value, onChange }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}</Label>
    <Input value={value || ''} onChange={e => onChange(e.target.value)} className="text-sm" />
  </div>
);
